import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
} from '@mui/icons-material';

// ─── Login Component ────────────────────────────────────────────────────────────
const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const notice = location.state?.notice;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        switch (result.user.role) {
          case 'admin':      navigate('/admin-dashboard');   break;
          case 'instructor': navigate('/creator-dashboard'); break;
          case 'student':    navigate('/studentportal');     break;
          default:           navigate('/');
        }
      } else {
        setError(result.error || t('auth.login.errorCredentials'));
      }
    } catch {
      setError(t('auth.login.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#09090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 6,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            component="img"
            src="/images/logo/logo-qorenova.png"
            alt="QoreNova"
            onError={(e) => { e.target.style.display = 'none'; }}
            sx={{ width: 48, height: 48, borderRadius: 2, mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#fafafa', letterSpacing: '-0.02em' }}>
            {t('auth.login.appName')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#71717a', mt: 0.5 }}>
            {t('auth.login.subtitle')}
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            bgcolor: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
          }}
        >
          {notice && !error && (
            <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(245,158,11,0.12)', color: '#fcd34d', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 2 }}>
              {notice}
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                bgcolor: 'rgba(239,68,68,0.1)',
                color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 2,
                '& .MuiAlert-icon': { color: '#ef4444' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label={t('auth.login.emailLabel')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                },
                '& .MuiInputLabel-root': { color: '#71717a' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#a855f7' },
                '& input': { color: '#fafafa' },
              }}
            />

            <TextField
              fullWidth
              label={t('auth.login.passwordLabel')}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#7c3aed' },
                },
                '& .MuiInputLabel-root': { color: '#71717a' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#a855f7' },
                '& input': { color: '#fafafa' },
                '& .MuiIconButton-root': { color: '#71717a' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="small"
                    sx={{ color: '#52525b', '&.Mui-checked': { color: '#7c3aed' } }}
                  />
                }
                label={<Typography variant="body2" sx={{ color: '#a1a1aa' }}>{t('auth.login.rememberMe')}</Typography>}
              />
              <Typography
                component={Link}
                to="/forgot-password"
                variant="body2"
                sx={{ color: '#a855f7', textDecoration: 'none', fontWeight: 500, '&:hover': { color: '#c084fc' } }}
              >
                {t('auth.login.forgotPassword')}
              </Typography>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                fontSize: '0.95rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(124,58,237,0.35)',
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                  boxShadow: '0 6px 28px rgba(124,58,237,0.45)',
                },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t('auth.login.signInBtn')}
            </Button>

            <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(255,255,255,0.08)' } }}>
              <Typography variant="caption" sx={{ color: '#52525b', px: 1 }}>{t('auth.login.orDivider')}</Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<GoogleIcon sx={{ fontSize: 20 }} />}
              disabled
              sx={{
                py: 1.4,
                mb: 3,
                borderColor: 'rgba(255,255,255,0.1)',
                color: '#71717a',
                fontWeight: 500,
                borderRadius: 2,
                '&:disabled': { borderColor: 'rgba(255,255,255,0.06)', color: '#52525b' },
              }}
            >
              {t('auth.login.googleBtn')} &nbsp;
              <Typography component="span" variant="caption" sx={{ color: '#52525b' }}>{t('auth.login.comingSoon')}</Typography>
            </Button>

            <Typography variant="body2" align="center" sx={{ color: '#71717a' }}>
              {t('auth.login.noAccount')}{' '}
              <Typography
                component={Link}
                to="/register"
                variant="body2"
                sx={{ color: '#a855f7', fontWeight: 600, textDecoration: 'none', '&:hover': { color: '#c084fc' } }}
              >
                {t('auth.login.createAccount')}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Footer note */}
        <Typography variant="caption" align="center" display="block" sx={{ color: '#3f3f46', mt: 4 }}>
          {t('auth.login.footerNote')}
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
